import { SPECIAL_SYMBOL_LIST } from '../../Search/constants/deepSearch';
import {
  AVAILABLE_DEEP_SEARCH_TAGS,
  CUSTOM_TAG_VALUE,
  SYSTEM_TAG_BRANDS,
  SYSTEM_TAG_CC,
  SYSTEM_TAG_PEOPLE,
  SYSTEM_TAG_TEXT,
  TYPE_ALL,
} from '../constants/label';
import { DIVIDER, END, HIGHLIGHT_MARK_REG_EXP, START } from '../constants/string';

import { LogListType, LogType } from '@/modules/common/types/deepSearch';

import { addHighlightMarks, escapeStringRegexp } from './string';

const isAcross = (aX: number, aLength: number, bX: number): boolean => bX >= aX && bX <= aLength;

export const getItemId = (
  type: string,
  value: string,
  color: string | undefined,
  name: string | undefined,
  time: number,
  endTime: number | undefined,
): string => ['item', type, color ?? '', name ?? '', encodeURIComponent(value), time, endTime ?? time].join('-');

const mergeUnique = (source: LogType[], next: LogType[]): LogType[] =>
  next.reduce(
    (acc, log) => {
      const alreadyInArray = acc.some(
        (log$) =>
          log.type === log$.type &&
          log.value === log$.value &&
          log.color === log$.color &&
          log.name === log$.name &&
          log.time === log$.time &&
          log.endTime === log$.endTime,
      );

      return !alreadyInArray ? acc.concat({ ...log }) : acc;
    },
    [...source],
  );

const mergeDuplicates = (logList: LogListType[]): LogListType[] => {
  const result: LogListType[] = [];

  logList.forEach((item) => {
    const sib = result.find(({ time }) => item.time === time);

    if (sib && sib.endTime === item.endTime) {
      sib.list = mergeUnique(sib.list, item.list);
    } else {
      result.push(item);
    }
  });

  return result;
};

const compressByTime = (logList: LogListType[], queries: string[]): LogListType[] => {
  const compressedLogList: LogListType[] = [];

  const addInItem = (log: LogListType, log$: LogListType) => {
    if (log.endTime && log$.endTime) {
      if (isAcross(log.time, log.endTime, log$.time)) {
        compressedLogList.push({
          time: Math.max(log$.time, log.time),
          endTime: Math.min(log$.endTime, log.endTime),
          list: mergeUnique(log.list, log$.list),
        });
      }
    } else if (log.endTime) {
      if (log$.time >= log.time && log$.time <= log.endTime) {
        compressedLogList.push({
          time: log$.time,
          endTime: undefined,
          list: mergeUnique(log.list, log$.list),
        });
      }
    } else if (log$.endTime) {
      if (log.time >= log$.time && log.time <= log$.endTime) {
        compressedLogList.push({
          time: log.time,
          endTime: undefined,
          list: mergeUnique(log.list, log$.list),
        });
      }
    } else if (log.time === log$.time) {
      compressedLogList.push({
        time: log.time,
        endTime: undefined,
        list: mergeUnique(log.list, log$.list),
      });
    }
  };

  for (let i = 0; i < logList.length; i += 1) {
    const targetLog = logList[i] as LogListType;

    const list = targetLog.list.filter(
      (log$) =>
        [SYSTEM_TAG_TEXT, SYSTEM_TAG_CC].some((type$) => log$.type === type$) &&
        queries.every((query) => log$.highlights.some(({ id }) => query === id)),
    );

    if (list.length) {
      compressedLogList.push({
        time: targetLog.time,
        endTime: targetLog.endTime,
        list,
      });
    }

    for (let k = i + 1; k < logList.length; k += 1) {
      const nextLog = logList[k];
      addInItem(targetLog, nextLog);
    }
  }

  return mergeDuplicates(compressedLogList)
    .filter((item) => {
      const ids = item.list.reduce((acc: string[], { highlights }) => [...acc, ...highlights.map(({ id }) => id)], []);
      return queries.every((query) => ids.includes(query));
    })
    .map((item) => {
      const endTime = item.time === item.endTime ? undefined : item.endTime;

      return {
        ...item,
        endTime,
        list: item.list.map((log) => ({
          ...log,
          time: item.time,
          endTime,
        })),
      };
    });
};

export const mergeTagsByTime = (logList: LogType[], queries: string[], includeAllTags: boolean): LogListType[] => {
  const formattedLogList = logList.reduce((acc, logSource) => {
    const log = {
      ...logSource,
      highlights: logSource.highlights.slice(),
    };

    const itemIndex = acc.findIndex(({ time, endTime }) => time === log.time && endTime === log.endTime);

    if (itemIndex === -1) {
      return acc.concat({
        time: log.time,
        endTime: log.endTime,
        list: [log],
      });
    }

    acc[itemIndex].list.push(log);
    return acc;
  }, [] as LogListType[]);

  const resultItems =
    includeAllTags && queries.length >= 2 ? compressByTime(formattedLogList, queries) : formattedLogList;

  return resultItems.map((item) => ({
    ...item,
    list: item.list.map((log) => ({
      ...log,
      itemId: getItemId(log.type, log.value, log.color, log.name, log.time, log.endTime),
    })),
  }));
};

type tabType = (typeof AVAILABLE_DEEP_SEARCH_TAGS)[number];

export const getTabList = (dsTags: tabType[]) => {
  const tabsToShow: (tabType | typeof TYPE_ALL)[] = [
    SYSTEM_TAG_PEOPLE,
    SYSTEM_TAG_BRANDS,
    SYSTEM_TAG_CC,
    SYSTEM_TAG_TEXT,
  ].filter((type) => dsTags.includes(type));

  if (dsTags.length > 1) {
    tabsToShow.unshift(TYPE_ALL);
  }

  return tabsToShow;
};

const getHLRegExp = (highlights: string[]): RegExp => {
  const hlPart = highlights.map((value) => `\\b${escapeStringRegexp(value)}\\b`).join('|');
  return new RegExp(`(${hlPart})`, 'g');
};

export const getSplitRegExp = (stopWords: string[]): RegExp => {
  const allRegSymbols = [stopWords.map((value) => `\b${value}\b`).join('|'), `[${SPECIAL_SYMBOL_LIST.join('')}]`].join(
    '|',
  );

  return new RegExp(`${END}${DIVIDER}((${allRegSymbols})+)?${DIVIDER}${START}`, 'gi');
};

export const getFormattedLogList = (string: string, highlights: string[], stopWords: string[], itemId: string) => {
  const resultString =
    string === highlights[0]
      ? addHighlightMarks(string)
      : string
          .replace(getHLRegExp(highlights), (match, $1) => ($1 ? addHighlightMarks($1) : ''))
          .replace(getSplitRegExp(stopWords), (match, $1) => $1);

  return resultString
    .split(DIVIDER)
    .map((text, index) => {
      let isHighlighted = false;
      const value = text.replace(HIGHLIGHT_MARK_REG_EXP, () => {
        isHighlighted = true;
        return '';
      });

      return {
        id: `${itemId}-${+index}`,
        isHighlighted,
        value,
      };
    })
    .filter(({ value }) => !!value);
};

export const getAvailableLogs = (data: LogType[], queries: string[]) =>
  data.reduce((acc, item) => {
    const highlightsList = item.highlights.filter(({ id }) => queries.some((id$) => id === id$));

    return !highlightsList.length
      ? acc
      : acc.concat({
          ...item,
          highlights: highlightsList,
        });
  }, [] as LogType[]);

export const getLogCountCollection = (
  data: LogType[],
  queries: string[],
  isIncludeAllTags: boolean,
): Record<string, number> => {
  const defaultCollection: Record<string, number> = {
    [TYPE_ALL]: 0,
    [SYSTEM_TAG_PEOPLE]: 0,
    [SYSTEM_TAG_BRANDS]: 0,
    [SYSTEM_TAG_TEXT]: 0,
    [SYSTEM_TAG_CC]: 0,
    [CUSTOM_TAG_VALUE]: 0,
  };

  if (isIncludeAllTags) {
    const lCollection = { ...defaultCollection };

    Object.keys(defaultCollection).forEach((type) => {
      const preparedData = type === TYPE_ALL ? data : data.filter((log) => log.type === type);

      mergeTagsByTime(getAvailableLogs(preparedData, queries), queries, isIncludeAllTags)
        .map((item$) => ({
          ...item$,
          list: item$.list.filter((log) => type === TYPE_ALL || log.type === type),
        }))
        .forEach((item) => {
          const ids = item.list.reduce(
            (acc: string[], { highlights }) => [...acc, ...highlights.map(({ id }) => id)],
            [],
          );

          if (queries.every((query) => ids.includes(query))) {
            lCollection[type] += item.list.length;
          }
        });
    });

    return lCollection;
  }

  const countsByType = mergeTagsByTime(getAvailableLogs(data, queries), queries, isIncludeAllTags).reduce(
    (acc, item) => {
      item.list.forEach((log) => {
        acc[log.type] += 1;
      });
      return acc;
    },
    { ...defaultCollection },
  );

  const sumCount = Object.keys(countsByType).reduce((acc, key) => acc + countsByType[key], 0);

  return {
    ...countsByType,
    [TYPE_ALL]: sumCount,
  };
};
